import type {
  AggregateOffer,
  Offer,
  PageInfo,
  Product,
  ProductListingPage,
} from "@decocms/apps-commerce/types";
import { canonicalFor } from "~/utils/constants";

interface BestOffer {
  price?: number;
  priceCurrency?: string;
  availability?: string;
}

export function getBestOffer(offers: Offer[] | AggregateOffer | undefined): BestOffer {
  if (!offers) return {};

  if ("@type" in offers && offers["@type"] === "AggregateOffer") {
    return {
      price: offers.lowPrice,
      priceCurrency: offers.priceCurrency,
      availability: offers.offers?.[0]?.availability,
    };
  }

  if (Array.isArray(offers) && offers.length > 0) {
    const best = offers.reduce((a, b) => {
      const ap = a.price ?? Infinity;
      const bp = b.price ?? Infinity;
      return ap <= bp ? a : b;
    });
    return {
      price: best.price,
      priceCurrency: best.priceCurrency,
      availability: best.availability,
    };
  }

  return {};
}

export function computeItemListOffset(pageInfo: PageInfo, fallbackPerPage: number): number {
  const perPage = pageInfo.recordPerPage || fallbackPerPage;
  const currentPage = pageInfo.currentPage || 1;
  return (currentPage - 1) * perPage;
}

// PLPJsonLd da lib (@decocms/blocks/hooks) numera a
// posição sempre a partir de 1 — página 2 de uma categoria repetiria as
// posições 1..N já usadas na página 1. Por isso montamos o ItemList aqui,
// na mesma forma de Product/Offer, mas com offset de paginação real.
export function buildCategoryItemList(page: ProductListingPage): Record<string, unknown> | null {
  const products = page.products ?? [];
  if (products.length === 0) return null;

  const offset = computeItemListOffset(page.pageInfo, products.length);

  const itemListElement = products.map((product: Product, index: number) => {
    const offer = getBestOffer(product.offers as Offer[] | AggregateOffer | undefined);
    const item: Record<string, unknown> = {
      "@type": "Product",
      name: product.isVariantOf?.name ?? product.name,
      url: canonicalFor(product.url),
      image: product.image?.[0]?.url,
    };

    if (offer.price != null) {
      item.offers = {
        "@type": "Offer",
        price: offer.price,
        priceCurrency: offer.priceCurrency ?? "BRL",
        availability: offer.availability ?? "https://schema.org/InStock",
      };
    }

    return {
      "@type": "ListItem",
      position: offset + index + 1,
      item,
    };
  });

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    url: page.seo?.canonical,
    name: withBrandSuffix(page.seo?.title),
    description: page.seo?.description,
    numberOfItems: page.pageInfo?.records ?? products.length,
    itemListElement,
  };
}

// Mirrors `ProductJsonLd` (@decocms/blocks/hooks) so PDP's own `<script>`
// render and the head()-level `jsonLDs` agree on shape — see SeoPDP.tsx.
export function buildProductJsonLd(
  product: Product,
  url?: string,
): Record<string, unknown> {
  const offer = getBestOffer(product.offers as Offer[] | AggregateOffer | undefined);
  const images = product.image?.map((img) => img.url).filter(Boolean) ?? [];
  const rating = product.aggregateRating;
  const resolvedUrl = url ?? product.url;

  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.isVariantOf?.name ?? product.name,
    description: product.description,
    image: images.length === 1 ? images[0] : images,
    url: resolvedUrl,
    sku: product.sku,
    productID: product.productID,
    brand: product.brand ? { "@type": "Brand", name: product.brand.name } : undefined,
    gtin: product.gtin,
  };

  if (offer.price != null) {
    data.offers = {
      "@type": "Offer",
      price: offer.price,
      priceCurrency: offer.priceCurrency ?? "BRL",
      availability: offer.availability ?? "https://schema.org/InStock",
      url: resolvedUrl,
    };
  }

  if (rating?.ratingValue) {
    data.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: rating.ratingValue,
      reviewCount: rating.reviewCount ?? rating.ratingCount ?? 0,
      bestRating: rating.bestRating ?? 5,
      worstRating: rating.worstRating ?? 1,
    };
  }

  return data;
}

const BRAND_SUFFIX = "Daniela Tombini";

export function withBrandSuffix(title: string | undefined): string | undefined {
  if (!title) return title;
  const trimmed = title.trim();
  if (!trimmed) return BRAND_SUFFIX;
  if (trimmed.toLowerCase().endsWith(BRAND_SUFFIX.toLowerCase())) return trimmed;
  return `${trimmed} | ${BRAND_SUFFIX}`;
}
